import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns-tz';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import uuid from 'uuid';

import { BlueCircleCheckmark } from 'assets/icons/blue-circle-checkmark';
import { EyeIcon } from 'assets/icons/eye-icon';
import { CaretDown } from 'src/app/components/icons';
import { ControlBar, ControlGroup, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { PageLoader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import {
  FooterSelect,
  PageUnits,
  PaginationControlContainer,
  PaginationRange,
  StatusContainer,
} from 'src/app/components/lib/table/footer/footer-rebrand.styles';
import { RebrandPagination } from 'src/app/components/lib/table/footer/rebrand-pagination';
import { Type } from 'src/app/components/lib/type';
import { SlideContainer } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/shared/styles';
import { useGetDutchieTemplateQuery } from 'src/app/queries/email-marketing/dutchie-template';
import { useGetTemplatesQuery } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import { useSaveCampaignTemplateMutation } from 'src/app/queries/marketing/campaign-templates/upsert-campaign-template';
import { useNotificationActions } from 'src/app/state/notifications';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

import { useDebounce } from '../../helpers';
import { HtmlPreviewIframe, RightSideControls, TemplateName, TemplatePreview } from '../../list/template-list.styles';
import { useTemplateBuilderContext } from '../../wizard/builder-context';
import { getCampaignTemplatesPayload } from '../../wizard/helpers';

import { TemplatePreviewModal } from './template-preview-modal';

import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';
import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

export function MarketingTemplateSelect({
  enableNextButton,
  disableNextButton,
  setComplete,
  setIncomplete,
  onNextButtonClick,
}: WizardStepComponentProps) {
  const navigate = useNavigate();
  const notifications = useNotificationActions();
  const params = useParams()?.['*'];
  const isEdit = useMemo(() => params?.includes('edit'), [params]);

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const debouncedSearchFilter = useDebounce(searchFilter, 1000);

  const { selectedCompany } = useRecoilValue(userDispensariesAtom);
  const { mutateAsync: saveCampaignTemplate } = useSaveCampaignTemplateMutation();

  const {
    data: campaignTemplates = { Data: [], Page: 0, TotalPages: 0, TotalRows: 0 },
    isLoading: isCampaignTemplatesLoading,
  } = useGetTemplatesQuery({
    CurrentPage: currentPage,
    PageSize: pageSize,
    Sorting: [],
    Filters:
      debouncedSearchFilter.length > 0
        ? [
            {
              Key: 'TemplateName',
              Operator: 'Like',
              Value: debouncedSearchFilter,
            },
          ]
        : [],
  });

  const { data: preExistingTemplates = [], isLoading: isPreExistingTemplatesLoading } = useGetDutchieTemplateQuery();

  // Combine templates and campaign templates for first page only, adding UUIDs to templates
  const rows = useMemo(() => {
    if (currentPage === 0) {
      // On first page, show templates first, then campaign templates
      // Filter pre-existing templates based on search term
      const filteredPreExistingTemplates =
        debouncedSearchFilter.length > 0
          ? preExistingTemplates?.filter((template) =>
              template.TemplateName.toLowerCase().includes(debouncedSearchFilter.toLowerCase())
            ) ?? []
          : preExistingTemplates ?? [];

      const preExistingTemplateData = filteredPreExistingTemplates.map((template) => ({
        ...template,
        Id: template.Id || uuid(), // Add UUID if not already present
      }));
      const campaignTemplateData =
        campaignTemplates.Data?.map((template) => ({
          ...template,
          Id: template.Id || uuid(), // Add UUID if not already present
        })) ?? [];
      return [...preExistingTemplateData, ...campaignTemplateData];
    }
    // On other pages, only show campaign templates with UUIDs
    return (
      campaignTemplates.Data?.map((template) => ({
        ...template,
        Id: template.Id || uuid(), // Add UUID if not already present
      })) ?? []
    );
  }, [currentPage, preExistingTemplates, campaignTemplates.Data, debouncedSearchFilter]);

  const handleSearchFilterChange = (value: string) => {
    setSearchFilter(value);
    setCurrentPage(0); // Reset to first page when search changes
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  const [builderState, dispatch] = useTemplateBuilderContext();
  const { marketingTemplatePresetId, selectedTemplate } = builderState;

  const setSelectedTemplate = (template: MarketingTemplatePreset | null) =>
    dispatch({ type: 'set-value', payload: { key: 'selectedTemplate', value: template } });

  const [selectedListItem, setSelectedListItem] = useState<string | null>(null);
  const selectedTemplateHTML = useMemo(() => {
    const preExistingAndCampaignTemplates = rows.find((t) => t.Id === selectedListItem);
    return preExistingAndCampaignTemplates?.HtmlBody;
  }, [selectedListItem, rows]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleItemClick = (campaignTemplate: MarketingTemplatePreset | null) => {
    setSelectedListItem(campaignTemplate?.Id ?? null);
    setSelectedTemplate(campaignTemplate);
  };

  // save draft in background before proceeding to next step
  const handleSaveOnNextStep = useCallback(async () => {
    const updatedFields: Record<string, any> = {};

    if (!builderState.templateName && !isEdit) {
      const formattedDate = format(new Date(), 'MM/dd/yyyy hh:mm a');
      const newTemplateName = `Template ${formattedDate}`;
      updatedFields.TemplateName = newTemplateName;

      dispatch({
        type: 'set-value',
        payload: {
          key: 'templateName',
          value: newTemplateName,
        },
      });
    }

    updatedFields.JsonBody = selectedTemplate?.JsonBody ?? '';
    updatedFields.HtmlBody = selectedTemplate?.HtmlBody ?? '';

    const payload = getCampaignTemplatesPayload(updatedFields, builderState);

    await saveCampaignTemplate(payload, {
      onSuccess: (data) => {
        dispatch({
          type: 'set-value',
          payload: { key: 'marketingTemplatePresetId', value: data.MarketingTemplatePresetId },
        });

        navigate(`/marketing/configure/templates/edit/${data.MarketingTemplatePresetId}`);
      },
      onError: (e) => {
        const error = e as ApiErrorResponse;
        const { data, status } = error.response;
        // a 409 error will return a user-friendly error message
        if (status === 409) {
          notifications.error({ message: data.Message });
        } else {
          notifications.error({ message: 'Error saving email template' });
        }
      },
    });
  }, [builderState, navigate, saveCampaignTemplate, selectedCompany, selectedTemplate]);

  useEffect(() => {
    onNextButtonClick(handleSaveOnNextStep);
  }, [handleSaveOnNextStep, onNextButtonClick]);

  useEffect(() => {
    if (selectedListItem || marketingTemplatePresetId) {
      setComplete();
      enableNextButton();
    } else {
      setIncomplete();
      disableNextButton();
    }
  }, [selectedListItem, marketingTemplatePresetId, enableNextButton, setComplete, setIncomplete, disableNextButton]);

  return (
    <SlideContainer>
      <MainContainer>
        <CardsContainer>
          <ControlBar>
            <ControlGroup grow>
              <StyledSearchTitle px={16} weight='semibold'>
                Choose a template
              </StyledSearchTitle>

              <ControlSearchBox
                initialValue={searchFilter}
                placeholder='Search templates...'
                onChange={handleSearchFilterChange}
              />
            </ControlGroup>
          </ControlBar>

          {isPreExistingTemplatesLoading || isCampaignTemplatesLoading ? (
            <PageLoader />
          ) : (
            <>
              {rows.map((template) => (
                <TemplateCard
                  key={template.Id}
                  selected={selectedListItem === template.Id}
                  onClick={() => handleItemClick(template)}
                >
                  {selectedListItem === template.Id && (
                    <BlueCircleCheckmarkContainer data-testid='blue-circle-checkmark'>
                      <BlueCircleCheckmark />
                    </BlueCircleCheckmarkContainer>
                  )}
                  <TemplatePreview>
                    <HtmlPreviewIframe
                      sandbox='allow-same-origin'
                      srcDoc={
                        template.HtmlBody ||
                        '<h1 style="font-size: 48px; text-align: center; padding: 20px; color: #999;">No preview available</h1>'
                      }
                    />
                  </TemplatePreview>
                  <TemplateFooter>
                    <TemplateName>{template.TemplateName}</TemplateName>

                    {template.TemplateName !== 'Blank' && (
                      <EyeButton data-testid='eye-button' onClick={() => setShowPreviewModal(true)}>
                        <EyeIcon />
                      </EyeButton>
                    )}
                  </TemplateFooter>
                </TemplateCard>
              ))}
            </>
          )}

          {rows.length > 0 && !isCampaignTemplatesLoading && (
            <StyledPaginationControlContainer>
              <RebrandPagination
                count={campaignTemplates.TotalPages}
                page={currentPage + 1}
                onChange={(event, value) => handlePageChange(value - 1)}
              />
              <RightSideControls>
                <StatusContainer>
                  Displaying{' '}
                  <PaginationRange>
                    {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, campaignTemplates.TotalRows)}
                  </PaginationRange>{' '}
                  of <span>{campaignTemplates.TotalRows}</span>
                </StatusContainer>
                <FooterSelect
                  IconComponent={CaretDown}
                  value={pageSize.toString()}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {[5, 10, 25].map((size) => (
                    <MenuItem key={String(size)} value={String(size)}>
                      {size}
                    </MenuItem>
                  ))}
                </FooterSelect>
                <PageUnits>Per page</PageUnits>
              </RightSideControls>
            </StyledPaginationControlContainer>
          )}
        </CardsContainer>
      </MainContainer>
      <TemplatePreviewModal
        isOpen={showPreviewModal}
        templateHTML={selectedTemplateHTML ?? ''}
        onClose={() => setShowPreviewModal(false)}
      />
    </SlideContainer>
  );
}
const MainContainer = styled.div`
  background-color: var(--color-brand-primary-white);
  border-radius: 8px;
  width: 100%;
  max-width: 850px;
  padding: var(--sizes-70);
  margin-top: var(--sizes-60);
`;
const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sizes-50);
  position: relative;
`;
const StyledSearchTitle = styled(Type)`
  min-width: 140px;
  width: 140px;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: center;
  margin-right: var(--sizes-40);
`;
const TemplateCard = styled.div<{ selected?: boolean }>`
  width: 248px;
  border: 1px solid var(--color-gray-20);
  outline: ${({ selected }) => (selected ? '3px solid var(--color-brand-secondary-sapphire)' : 'none')};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  position: relative;
`;
const BlueCircleCheckmarkContainer = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
`;
const EyeButton = styled.div`
  border-radius: 4px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-gray-70);
  width: 38px;
  height: 30px;

  :hover {
    background-color: var(--color-gray-60);
  }
`;
const TemplateFooter = styled.div`
  padding: var(--sizes-40);
  height: 54px;
  background-color: var(--color-brand-primary-white);
  border-top: 1px solid var(--color-gray-20);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledPaginationControlContainer = styled(PaginationControlContainer)`
  border-top: 1px solid var(--color-gray-20);
  padding: var(--sizes-20);
  margin-top: var(--sizes-60);
  width: 100%;
  justify-content: space-between;
  padding-left: var(--sizes-100);
  padding-right: var(--sizes-100);

  @media (max-width: 1024px) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }

  @media (max-width: 640px) {
    margin-left: calc(-1 * var(--sizes-60));
    margin-right: calc(-1 * var(--sizes-60));
    width: calc(100% + 2 * var(--sizes-60));
  }
`;
